
import { action, computed, makeObservable, observable } from 'mobx';
import dayjs, { Dayjs } from 'dayjs';

import { StoreNode } from '../../../store';
import { Store } from '../../../store/store';
import { DateInputProps, Info, RangeOptions } from './dateInput';
import type { PanelMode } from './dateInput';

export class DateInputState extends StoreNode {
  readonly nodeType = 'Datepicker';
  constructor(store: Store, props: DateInputProps) {
    super(store, props);
    makeObservable<this, 'computeMaxValue'>(this, {
      isDirty: computed,
      isRangeDatepicker: computed,
      placeholder: computed,
      validationMessage: computed,
      inputReadOnly: computed,
      allowEmpty: computed,
      isRequired: computed,
      disabled: computed,
      datepickerId: computed,
      format: computed,
      shouldDisableDates: computed,
      defaultPickerValue: computed,
      rangeOptions: computed,
      previousSelectedComputedValue: computed,
      activeRangeComputedValue: computed,
      error: computed,
      formattedValue: computed,
      isTouched: observable,
      isSubmitting: observable,
      open: observable,
      variant: observable,
      max: observable,
      min: observable,
      activeRangeOption: observable,
      previousSelectedRange: observable,
      mode: observable,
      value: observable,
      initialValue: observable,
      previousSelectedValue: observable,
      handleChange: action,
      computeMaxValue: action,
      handleSubmit: action,
      handleOpenChange: action,
      onBackdropClick: action,
      cancel: action,
      apply: action,
      disabledDate: action,
      setActiveRange: action,
      onCalendarChange: action,
      onPanelChange: action,
      loadValue: action,
      reset: action,
    });

    const { activeRangeOption, defaultPickerValue } = props;

    this.activeRangeOption = activeRangeOption || null;
    this.previousSelectedRange = activeRangeOption || null;
    this.value = activeRangeOption ? activeRangeOption.getRange() : defaultPickerValue ?? null;
    this.previousSelectedValue = activeRangeOption ? activeRangeOption.getRange() : null;
  }

  get isDirty(): boolean {
    if (
      Array.isArray(this.value) &&
      this.value[0].diff((this.initialValue as [Dayjs, Dayjs])?.[0]) &&
      this.value[1].diff((this.initialValue as [Dayjs, Dayjs])?.[1])) {
      return true;
    }

    if ((this.value as Dayjs)?.diff(this.initialValue as Dayjs)) return true;
    return false;
  }

  get isRangeDatepicker(): boolean {
    return this.resolvedProps.rangeDatePicker ?? true;
  }

  get placeholder(): [string, string] | string | null {
    return this.resolvedProps.placeholder;
  }

  get validationMessage(): string | null {
    return this.resolvedProps.validationMessage;
  }

  get inputReadOnly(): boolean {
    return this.resolvedProps.inputReadOnly || false;
  }

  get allowEmpty(): [boolean, boolean] {
    return this.resolvedProps.allowEmpty || [false, false];
  }

  get isRequired(): boolean {
    return this.resolvedProps.isRequired;
  }

  get disabled(): boolean {
    return this.resolvedProps.disabled || false;
  }

  get datepickerId(): string {
    return this.resolvedProps.id || 'antd-range-picker';
  }

  get format(): string | string[] {
    return this.resolvedProps.format || ['DD/MM/YYYY', 'DDMMYYYY'];
  }

  get shouldDisableDates(): boolean {
    return this.resolvedProps.shouldDisableDates || false;
  }

  get defaultPickerValue(): [Dayjs, Dayjs] | Dayjs | null {
    return this.resolvedProps.defaultPickerValue;
  }
  get rangeOptions(): RangeOptions[] {
    return this.resolvedProps.rangeOptions || [];
  }

  get previousSelectedComputedValue(): [Dayjs, Dayjs] | Dayjs | null {
    return this.previousSelectedValue;
  }
  get activeRangeComputedValue(): [Dayjs, Dayjs] | null {
    return this.activeRangeOption?.getRange() || null;
  }

  get error(): string | null {
    if (
      this.isRequired &&
      !this.value &&
      (this.isTouched || this.isSubmitting))
      return 'Required field';

    return null;
  }

  // TODO: refactor the datepicker to avoid needing this workaround for formatted output
  get formattedValue(): string | null {
    if (!this.value) return null;

    return dayjs(this.value as Dayjs).format();
  }

  isTouched: boolean = false;
  isSubmitting: boolean = false;
  open: boolean = false;
  variant: 'outlined' | 'borderless' = 'borderless';
  max: Dayjs | null = null;
  min: Dayjs | null = dayjs();
  activeRangeOption: RangeOptions | null = null;
  previousSelectedRange: RangeOptions | null = null;
  mode: [string, string] | string | null = !this.isRangeDatepicker ? 'date' : ['date', 'date'];
  value: [Dayjs, Dayjs] | Dayjs | null = this.activeRangeComputedValue;
  initialValue: [Dayjs, Dayjs] | Dayjs | null = this.defaultPickerValue ?? null;
  previousSelectedValue: [Dayjs, Dayjs] | Dayjs | null = this.activeRangeComputedValue;


  handleChange = (values: [Dayjs | null, Dayjs | null] | Dayjs | null, _: [string, string] | string | null) => {
    this.value = values as [Dayjs, Dayjs] | Dayjs | null;
    // When default active range is provided value should not be null (e.g. Zoom Calendar where is mandatory to provide a range of maximum 1 month)
    if (this.isRangeDatepicker &&
      (values === null || (values as [Dayjs | null, Dayjs | null])[1] === null || (values as [Dayjs | null, Dayjs | null])[0] === null)) {
      if (this.props.activeRangeOption) {
        const range = this.props.activeRangeOption.getRange();
        this.value = range;
        this.max = range[1];
        this.activeRangeOption = this.props.activeRangeOption;
      } else {
        this.variant = 'borderless';
        this.activeRangeOption = null;
      }
    }

    this.computeMaxValue(values);
  }

  private computeMaxValue(values: [Dayjs | null, Dayjs | null] | Dayjs | null) {
    const rangeValues = values as [Dayjs | null, Dayjs | null] | null;
    if (this.shouldDisableDates && this.value && rangeValues && rangeValues[0]) {
      const computedMaxValue = rangeValues[0]?.add(31, 'day');
      this.max = computedMaxValue;

      (this.value as [Dayjs, Dayjs])[0] = rangeValues[0]!;
      if ((this.value as [Dayjs, Dayjs])[1]?.isAfter(this.max)) {
        (this.value as [Dayjs, Dayjs])[1] = null as unknown as Dayjs;
      }
    }
  }

  handleSubmit = () => {
    this.isSubmitting = true;
    this.invokeHandler('onSubmit');
  }

  handleOpenChange = (value: boolean) => {
    this.isTouched = true;

    if (value) {
      this.open = value;
      this.variant = 'outlined';
      if (this.activeRangeOption) this.computeMaxValue(this.activeRangeComputedValue);
    }
  }

  onBackdropClick = () => {
    if (this.open) {
      this.open = false;
      this.mode = !this.isRangeDatepicker ? 'date' : ['date', 'date'];
    }
  }

  cancel = () => {
    this.open = false;
    this.mode = !this.isRangeDatepicker ? 'date' : ['date', 'date'];
    this.value = this.previousSelectedComputedValue;
    this.activeRangeOption = this.previousSelectedRange;

    if (!this.value) this.variant = 'borderless';
  }

  apply = () => {
    this.open = false;
    this.mode = !this.isRangeDatepicker ? 'date' : ['date', 'date'];
    this.previousSelectedValue = this.value;
    this.previousSelectedRange = this.activeRangeOption;

    if (!this.value && !this.props.activeRangeOption) this.variant = 'borderless';
    //  If any (or both) of the values are empty when a default range is provided, use the default range when applying the dates
    const value = this.value as [Dayjs, Dayjs];
    if ((!this.value || !value[1] || !value[0]) && this.props.activeRangeOption) {
      const range = this.props.activeRangeOption.getRange();
      this.value = range;
      this.max = range[1];
      this.activeRangeOption = this.props.activeRangeOption;
      this.previousSelectedRange = this.props.activeRangeOption;
    }
  }

  disabledDate = (currentDate: Dayjs) => {
    if (this.max) {
      return currentDate?.isAfter(this.max);
    }

    if (this.min) {
      return currentDate?.isBefore(this.min);
    }
    return false;
  }

  setActiveRange = (selectedRange: RangeOptions) => {
    const selectedRangeValues = selectedRange?.getRange();
    const activeRangeValues = this.activeRangeOption?.getRange();

    if (selectedRangeValues && activeRangeValues) {
      // Check if the selected range is the same as the active one and disable it
      const isSameRange = activeRangeValues[0]?.isSame(selectedRangeValues[0]) && activeRangeValues[1]?.isSame(selectedRangeValues[1]);
      if (isSameRange) {
        this.value = null;
        this.activeRangeOption = null;
        return;
      }
    }

    this.activeRangeOption = selectedRange;
    this.handleChange(selectedRangeValues, ['', '']);
  }

  onCalendarChange = (values: [Dayjs | null, Dayjs | null], dateStrings: [string, string], info: Info) => {
    // When a preset range is selected, and a new value is choosen manually by the user from the picker, make sure to reset the current active range option 
    const activeRange = this.activeRangeComputedValue;
    if (this.value && activeRange) {
      this.activeRangeOption = null;
    }

    // Typed dates only reach `onCalendarChange` until the picker commits, so keep them for Apply (QA BUG-039).
    if (this.isRangeDatepicker && values?.[0] && values?.[1])
      this.value = values as [Dayjs, Dayjs];

    if (info.range === 'start') {
      this.computeMaxValue(values);
    }
  }

  onPanelChange = (_: Dayjs | [Dayjs | null, Dayjs | null] | null, mode: PanelMode | [PanelMode, PanelMode]) => {
    this.mode = this.isRangeDatepicker ? [mode[0], mode[1]] : mode as string;
  }

  loadValue(value: string | null) {
    if (value) {
      this.value = dayjs(value, 'YYYY-MM-DD');
      this.initialValue = dayjs(value, 'YYYY-MM-DD');
    }

    this.min = dayjs();
  }

  reset() {
    const { activeRangeOption } = this.props;
    this.activeRangeOption = activeRangeOption || null;
    this.previousSelectedRange = activeRangeOption || null;
    this.value = activeRangeOption ? activeRangeOption.getRange() : null;
    this.initialValue = null;
    this.previousSelectedValue = activeRangeOption ? activeRangeOption.getRange() : null;

    this.open = false;
    this.isSubmitting = false;
    this.isTouched = false;
    this.variant = 'borderless';
    this.max = null;
    this.min = null;
    this.mode = !this.isRangeDatepicker ? 'date' : ['date', 'date'];
  }
}

export const dateInput = (node: StoreNode | Store, props: DateInputProps) => {
  let store: Store;
  if (node instanceof StoreNode)
    store = node.store;
  else
    store = node;

  return new DateInputState(store, props);
}
